import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";

import CheckTreePicker from "rsuite/CheckTreePicker";
import Table from "rsuite/Table";
import DateRangePicker from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import "rsuite/Table/styles/index.css";
import "rsuite/CheckTreePicker/styles/index.css";
import "./style.scss";
import { useGetStatisticByPlanMutation } from "../../redux/api";

const { Column, HeaderCell, Cell } = Table;

const Statistic = () => {
    const [selectedPlans, setSelectedPlans] = useState<(string|number)[]>([]);
    const [getStatisticByPlans, {
        isLoading,
        isError,
    }] = useGetStatisticByPlanMutation();

    const { projectId, myGroupsAndPlans, statisticPage } = useAppSelector(
        (state: RootState) => state.windowSlice
    );

    const data = myGroupsAndPlans.map(group => ({
        label: group.title,
        value: group.id,
        children: group.plans.map(plan => ({
            label: plan.title,
            value: plan.id,
        })),
    }));

    const plans = statisticPage || [];
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);


    //getStatisticByPlans project_id: string, payment_types: string[] = ["success"], start_date: string, end_date: string, plans_ids: string[] 

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "short",
        });
    };

    const handleDateChange = (range: [Date, Date] | null) => {
        setDateRange(range);
    };
    

    const getCurrencies = (plan) => {
        const successPayments = plan.payments.find((payment) => payment.type === "success");
        if (!successPayments) return [];
    
        const currencies = new Set();
        Object.values(successPayments.data).forEach((records: any) => {
            records.forEach(({ currency }) => {
                currencies.add(currency);
            });
        });
    
        return Array.from(currencies);
    };
    

    const transformDataForTable = (plan) => {
        const successPayments = plan.payments.find((payment) => payment.type === "success");
        if (!successPayments) return [];
    
        // Преобразование данных
        const allCurrencies = ["USD", "EUR", "BYN", "PLN"]; // Добавьте нужные валюты здесь
        const totals = {}; // Для хранения итогов
        allCurrencies.forEach((currency) => {
            totals[`${currency} Count`] = 0;
            totals[`${currency} Amount`] = 0;
        });
    
        const tableData = Object.entries(successPayments.data).map(([time, records]: any) => {
            const row = { time: formatDate(time) }; // Форматируем дату
    
            records.forEach(({ amount, count, currency }) => {
                row[`${currency} Count`] = (row[`${currency} Count`] || 0) + count;
                row[`${currency} Amount`] = (row[`${currency} Amount`] || 0) + amount;
    
                // Суммируем значения для итогов
                totals[`${currency} Count`] += count;
                totals[`${currency} Amount`] += amount;
            });
    
            // Устанавливаем нули для отсутствующих валют
            allCurrencies.forEach((currency) => {
                if (!row[`${currency} Count`]) row[`${currency} Count`] = 0;
                if (!row[`${currency} Amount`]) row[`${currency} Amount`] = 0;
                row[`${currency} Amount`] = row[`${currency} Amount`].toFixed(2); // Форматируем сумму
            });
    
            return row;
        });
    
        // Добавляем итоговую строку
        const totalRow = { time: "Итого" }; // Заголовок итоговой строки
        allCurrencies.forEach((currency) => {
            totalRow[`${currency} Count`] = totals[`${currency} Count`];
            totalRow[`${currency} Amount`] = totals[`${currency} Amount`].toFixed(2); // Форматируем сумму
        });
        tableData.push(totalRow);
    
        return tableData;
    };
    
    const renderTable = (plan) => {
        const tableData = transformDataForTable(plan);
        const currencies = getCurrencies(plan); // Динамически получаем валюты
    
        if (tableData.length === 0) {
            return null;
        }
        return (
            <div key={plan.plan_id}>
                <h6 className="plan-title">{plan.plan_title}</h6>
                <span className="plan-title">{plan.price.amount} {plan.price.currency}</span>
                <Table
                    data={tableData}
                    headerHeight={55}
                    rowHeight={30}
                    wordWrap="break-word"
                    autoHeight
                    style={{ marginTop: "15px", borderRadius: "10px", background: "white" }}>
                    <Column width={100} align="center" fixed>
                        <HeaderCell>Дата</HeaderCell>
                        <Cell dataKey="time" />
                    </Column>
                    {currencies.map((currency: any) => (
                        <>
                            <Column width={80} align="center" key={`${currency} Count`}>
                                <HeaderCell align="center">Кол-во<br/>({currency})</HeaderCell>
                                <Cell dataKey={`${currency} Count`} />
                            </Column>
                            <Column width={100} align="center" key={`${currency} Amount`}>
                                <HeaderCell align="center">Сумма<br/>({currency})</HeaderCell>
                                <Cell dataKey={`${currency} Amount`} />
                            </Column>
                        </>
                    ))}
                </Table>
            </div>
        );
    };

    const calculateOverallSummary = () => {
        const summary = [];
        const allCurrencies = ["BYN", "EUR"];
        const totals = {};

        allCurrencies.forEach(currency => {
            totals[`${currency} Count`] = 0;
            totals[`${currency} Amount`] = 0;
        });

        plans.forEach(plan => {
            const planTotals = {
                planTitle: plan.plan_title,
                basePrice: `${plan.price.amount.toFixed(2)} ${plan.price.currency}`,
            };

            allCurrencies.forEach(currency => {
                planTotals[`${currency} Count`] = 0;
                planTotals[`${currency} Amount`] = 0;
            });
            

            plan.payments.forEach(payment => {
                if (payment.type === "success") {
                    Object.values(payment.data).forEach(records => {
                        records.forEach(({ currency, count, amount }) => {
                            planTotals[`${currency} Count`] += count;
                            planTotals[`${currency} Amount`] += amount;

                            totals[`${currency} Count`] += count;
                            totals[`${currency} Amount`] += amount;
                        });
                    });
                }
            });

            allCurrencies.forEach(currency => {
                planTotals[`${currency} Count`] = planTotals[`${currency} Count`];
                planTotals[`${currency} Amount`] = planTotals[`${currency} Amount`].toFixed(2);
            });
            

            summary.push(planTotals);
        });

        const totalRow = {
            planTitle: "Итого",
            basePrice: "-",
        };

        allCurrencies.forEach(currency => {
            totalRow[`${currency} Count`] = totals[`${currency} Count`];
            totalRow[`${currency} Amount`] = totals[`${currency} Amount`].toFixed(2);
            console.log(totals[`${currency} Amount`].toFixed(2));
        });

        summary.push(totalRow);
        return summary;
    };

    const renderOverallTable = () => {
        const summaryData = calculateOverallSummary();
        const allCurrencies = ["BYN", "EUR"];

        return (
            <div className="plan-table">
                <h6 className="plan-title">Сводная таблица</h6>
                <Table
                    data={summaryData}
                    headerHeight={55}
                    rowHeight={30}
                    wordWrap="break-word"
                    autoHeight
                    style={{ marginTop: "15px", borderRadius: "10px", background: "white", marginBottom: "25px" }}
                >
                    <Column width={200} align="start" fixed>
                        <HeaderCell>Название</HeaderCell>
                        <Cell dataKey="planTitle" />
                    </Column>
                    <Column width={150} align="start">
                        <HeaderCell>Цена</HeaderCell>
                        <Cell dataKey="basePrice" />
                    </Column>
                    {allCurrencies.map(currency => (
                        <>
                            <Column width={150} align="center" key={`${currency} Count`}>
                                <HeaderCell>Кол-во ({currency})</HeaderCell>
                                <Cell dataKey={`${currency} Count`} />
                            </Column>
                            <Column width={150} align="center" key={`${currency} Amount`}>
                                <HeaderCell>Сумма ({currency})</HeaderCell>
                                <Cell dataKey={`${currency} Amount`} />
                            </Column>
                        </>
                    ))}
                </Table>
            </div>
        );
    };
    
    
    const handleSelectChange = (selected: (string | number)[]) => {
        const selectedPlanIds: (string | number)[] = [];
    
        selected.forEach((id) => {
            // Проверяем, является ли ID группой
            const group = data.find((group) => group.value === id);
    
            if (group) {
                // Если ID группы, добавляем все тарифы (дочерние элементы)
                group.children.forEach((plan) => {
                    if (!selectedPlanIds.includes(plan.value)) {
                        selectedPlanIds.push(plan.value);
                    }
                });
            } else {
                // Если ID тариф, просто добавляем его
                if (!selectedPlanIds.includes(id)) {
                    selectedPlanIds.push(id);
                }
            }
        });
    
        setSelectedPlans(selectedPlanIds);
        console.log("Selected plans:", selectedPlanIds);
    };

    const handleBlur = () => {
        if (selectedPlans.length === 0) {
            console.log("No plans selected");
            return;
        }
    
        if (!dateRange) {
            console.log("No date range selected");
            return;
        }
    
        const [startDate, endDate] = dateRange;
    
        // Убедимся, что даты правильно форматируются и отправляются в нужном формате
        const formattedStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000)
            .toISOString()
            .split("T")[0];
        const formattedEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000)
            .toISOString()
            .split("T")[0];
    
        getStatisticByPlans({
            project_id: projectId,
            payment_types: ["success"],
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            plans_ids: selectedPlans,
        })
            .unwrap()
            .then((response) => {
                console.log("Statistics fetched successfully:", response);
            })
            .catch((error) => {
                console.error("Error fetching statistics:", error);
            });
    };
    
    

    return (
        <div className="statistic">
            <div className="statistic_container">
                <div className="statistic_wrapper">
                <DateRangePicker 
                    format="dd.MM.yyyy" 
                    style={{ width: "100%", marginBottom: "15px" }}
                    size="lg"
                    placeholder="Выберите период"
                    onChange={handleDateChange} // Обновляем состояние
                    showOneCalendar
                />

                <CheckTreePicker
                    defaultExpandAll
                    data={data}
                    style={{ width: "100%", marginBottom: "25px" }}
                    size="lg"
                    treeHeight={300}
                    locale={{
                        placeholder: "Выберите тарифы",
                        searchPlaceholder: "Поиск",
                        noResultsText: "Нет результатов",
                    }}
                    onChange={(values) => handleSelectChange(values)} // Pass the selected values
                    onBlur={handleBlur}
                />
                    {renderOverallTable()}
                    {plans.map((plan) => renderTable(plan))}
                </div>
            </div>
        </div>
    );
};

export default Statistic;